/**
 * AreaShiftTable — Areas by Shift breakdown (Day/Evening/Night).
 *
 * Grandstream campuses get a Response Time | Time to Connect toggle
 * (docs/TIME_TO_CONNECT_PLAN.md, phase 3) that swaps each shift's Avg Resp column for
 * avg_time_to_connect_1st/2nd/3rd from sp_getAreaTypeSummaryShifts v2. Avg Daily stays.
 */

import { useMemo, useState } from 'react';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import KpiToggle from '../../../../components/v2/KpiToggle.jsx';
import { TIME_TO_CONNECT_INFO, TIME_TO_CONNECT_NA } from '../../../reports/alert-details/timeToConnect.js';

const METRICS = [
    { key: 'response', label: 'Response Time' },
    { key: 'connect', label: 'Time to Connect' },
];

/* A shift with no alerts has nothing to measure ('-', as for response). With alerts
   but no intercom call: N/A (operations rule). MC has no speakers, so it reads N/A. */
const connectCell = (avgDaily, value) => {
    if (!(Number(avgDaily) > 0)) return '-';
    return value ?? TIME_TO_CONNECT_NA;
};

const AREAS_BY_SHIFT_TOOLTIP = [
    'This table breaks down the average daily alerts and average response time by area type for each shift over the last 28 days.',
    'Avg Daily is the average number of alerts per day occurring during that shift.',
    'Avg Resp is the average time, in minutes, from when an alert was triggered to when a caregiver acknowledged it.',
    'Shift hours follow your campus shift schedule when configured, otherwise the system defaults are used.',
];

/* ── Shift hour range formatting ── */
const formatHour = (h) => {
    const norm = ((h % 24) + 24) % 24;
    if (norm === 0) return '12am';
    if (norm === 12) return '12pm';
    if (norm < 12) return `${norm}am`;
    return `${norm - 12}pm`;
};

const buildShiftRange = (hourShift, shiftName) => {
    if (!hourShift || !shiftName) return '';
    const hours = Object.entries(hourShift)
        .filter(([, name]) => name === shiftName)
        .map(([h]) => parseInt(h, 10))
        .filter((h) => !isNaN(h))
        .sort((a, b) => a - b);

    if (hours.length === 0) return '';

    // Build contiguous segments
    const segments = [];
    let segStart = hours[0];
    let prev = hours[0];
    for (let i = 1; i < hours.length; i++) {
        if (hours[i] === prev + 1) {
            prev = hours[i];
        } else {
            segments.push([segStart, prev]);
            segStart = hours[i];
            prev = hours[i];
        }
    }
    segments.push([segStart, prev]);

    // Wrap-around: e.g. Night shift covering 22,23,0,1,2,3 → two segments [0..3] and [22..23]
    if (segments.length === 2 && segments[0][0] === 0 && segments[1][1] === 23) {
        return `${formatHour(segments[1][0])} - ${formatHour((segments[0][1] + 1) % 24)}`;
    }

    return segments.map(([s, e]) => `${formatHour(s)} - ${formatHour((e + 1) % 24)}`).join(', ');
};

const AreaShiftTable = ({ data, hourChartData, isGrandstream = false }) => {
    const rows = Array.isArray(data) ? data : [];
    const [metricChoice, setMetric] = useState('response');
    // A non-Grandstream campus can't be left on Time to Connect after a campus switch.
    const connect = isGrandstream && metricChoice === 'connect';
    const metricHeader = connect ? 'Avg Connect (min)' : 'Avg Resp (min)';
    const metricCell = (r, pos) => (connect
        ? connectCell(r[`avg_daily_events_${pos}`], r[`avg_time_to_connect_${pos}`])
        : (r[`avg_response_time_${pos}`] ?? '-'));

    const shiftRanges = useMemo(() => {
        const hourShift = hourChartData?.hourShift || {};
        return {
            day:     buildShiftRange(hourShift, 'Day'),
            evening: buildShiftRange(hourShift, 'Evening'),
            night:   buildShiftRange(hourShift, 'Night'),
        };
    }, [hourChartData]);

    const renderShiftLabel = (name, range) => (
        <span className="md-shift-label">
            {name}
            {range ? <span style={{ marginLeft: '6px', fontWeight: 'var(--font-weight-normal)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>({range})</span> : null}
        </span>
    );

    return (
        <div className="md-section">
            <div className="md-section__header">
                <h3 className="md-section__title">
                    Areas by Shift
                    <HeaderInfoTip
                        title="About This Table"
                        body={connect
                            ? `${AREAS_BY_SHIFT_TOOLTIP[0]} ${AREAS_BY_SHIFT_TOOLTIP[1]} Avg Connect is the average Time to Connect, in minutes. ${TIME_TO_CONNECT_INFO} ${AREAS_BY_SHIFT_TOOLTIP[3]}`
                            : AREAS_BY_SHIFT_TOOLTIP.join(' ')}
                    />
                </h3>
                {isGrandstream
                    ? <KpiToggle options={METRICS} value={connect ? 'connect' : 'response'} onChange={setMetric} label="Shift table metric" />
                    : null}
            </div>
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th className="dp-th" rowSpan={2}>Area Type</th>
                            <th className="dp-th md-shift--day dp-th--center" colSpan={2}>
                                {renderShiftLabel('Day', shiftRanges.day)}
                            </th>
                            <th className="dp-th md-shift--evening dp-th--center" colSpan={2}>
                                {renderShiftLabel('Evening', shiftRanges.evening)}
                            </th>
                            <th className="dp-th md-shift--night dp-th--center" colSpan={2}>
                                {renderShiftLabel('Night', shiftRanges.night)}
                            </th>
                        </tr>
                        <tr>
                            <th className="dp-th md-shift--day dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Daily</th>
                            <th className="dp-th md-shift--day dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>{metricHeader}</th>
                            <th className="dp-th md-shift--evening dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Daily</th>
                            <th className="dp-th md-shift--evening dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>{metricHeader}</th>
                            <th className="dp-th md-shift--night dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Daily</th>
                            <th className="dp-th md-shift--night dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>{metricHeader}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr className="dp-tr"><td className="dp-td dp-td--center" colSpan={7}><span className="dp-cell-text dp-cell-text--muted">No shift data</span></td></tr>
                        ) : null}
                        {rows.map((r, i) => (
                            <tr key={i} className="dp-tr">
                                <td className="dp-td" style={{ fontWeight: r.area_type_name === 'Totals' ? 600 : 'normal' }}>{r.area_type_name || '—'}</td>
                                <td className="dp-td md-shift--day dp-td--center">{r.avg_daily_events_1st ?? '-'}</td>
                                <td className="dp-td md-shift--day dp-td--center">{metricCell(r, '1st')}</td>
                                <td className="dp-td md-shift--evening dp-td--center">{r.avg_daily_events_2nd ?? '-'}</td>
                                <td className="dp-td md-shift--evening dp-td--center">{metricCell(r, '2nd')}</td>
                                <td className="dp-td md-shift--night dp-td--center">{r.avg_daily_events_3rd ?? '-'}</td>
                                <td className="dp-td md-shift--night dp-td--center">{metricCell(r, '3rd')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AreaShiftTable;
